<script setup>
import { onMounted, ref, defineProps, defineEmits, defineExpose, watch, nextTick, onBeforeUnmount, computed } from 'vue';
import { useIcons } from '@/composables/icons.js';

const { User } = useIcons();

const props = defineProps({
  modelValue: {
    type: [String, Number],
    default: ''
  },
  options: {
    type: Array,
    required: true
  },
  id: {
    type: String,
    required: true
  },
  placeholder: {
    type: String,
    default: ''
  },
  required: {
    type: Boolean,
    default: false
  },
  disabled: {
    type: Boolean,
    default: false
  },
  showProfilePicture: {
    type: Boolean,
    default: false
  },
  allowCreate: {
    type: Boolean,
    default: false
  },
  createText: {
    type: String,
    default: 'Create new'
  },
  noResultsText: {
    type: String,
    default: 'No results found'
  },
  clearable: {
    type: Boolean,
    default: false
  },
  loading: {
    type: Boolean,
    default: false
  }
});

const emit = defineEmits(['update:modelValue', 'create-new', 'clear']);
const input = ref(null);
const isOpen = ref(false);
const containerRef = ref(null);
const searchQuery = ref('');

const normalizeText = (text) => {
  return text
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/\s+/g, '')
    .trim();
};

const filteredOptions = computed(() => {
  if (!searchQuery.value) return props.options;
  const query = normalizeText(searchQuery.value);
  return props.options.filter(option => {
    const optionText = normalizeText(option.label || option.displayValue || '');
    return optionText.includes(query);
  });
});

const hasExactMatch = computed(() => {
  if (!searchQuery.value) return false;
  const query = normalizeText(searchQuery.value);
  return props.options.some(option => {
    const optionText = normalizeText(option.label || option.displayValue || '');
    return optionText === query;
  });
});

const showCreateOption = computed(() => {
  return props.allowCreate && 
         searchQuery.value && 
         !hasExactMatch.value && 
         filteredOptions.value.length === 0;
});

const handleInput = (event) => {
  const value = event.target.value;
  searchQuery.value = value;
  emit('update:modelValue', value);
  isOpen.value = true;
};

const displayValue = computed(() => {
  if (searchQuery.value) return searchQuery.value;
  const selectedOption = props?.options?.find(option => option?.value === props?.modelValue);
  return selectedOption ? selectedOption?.label || selectedOption?.displayValue : '';
});

const handleSelect = (option) => {
  if (props.disabled) return;
  searchQuery.value = '';
  emit('update:modelValue', option.value);
  isOpen.value = false;
};

const handleCreate = () => {
  if (props.disabled) return;
  emit('create-new', searchQuery.value);
  searchQuery.value = '';
  isOpen.value = false;
};

const handleClear = (event) => {
  if (props.disabled) return;
  event.stopPropagation();
  searchQuery.value = '';
  emit('update:modelValue', '');
  emit('clear');
  isOpen.value = false;
};

const handleFocus = () => {
  if (props.disabled) return;
  isOpen.value = true;
};

const handleBlur = () => {
  setTimeout(() => {
    isOpen.value = false;
  }, 200);
};

const updateDropdownPosition = () => {
  if (!isOpen.value || !containerRef.value) return;
  
  const dropdown = containerRef.value.querySelector('.combobox__dropdown');
  if (dropdown) {
    dropdown.style.width = '100%';
  }
};

watch(isOpen, (newValue) => {
  if (newValue) {
    nextTick(() => {
      updateDropdownPosition();
      window.addEventListener('scroll', updateDropdownPosition);
      window.addEventListener('resize', updateDropdownPosition);
    });
  } else {
    window.removeEventListener('scroll', updateDropdownPosition);
    window.removeEventListener('resize', updateDropdownPosition);
  }
});

onMounted(() => {
  const handleClickOutside = (event) => {
    if (containerRef.value && !containerRef.value.contains(event.target)) {
      isOpen.value = false;
    }
  };
  document.addEventListener('click', handleClickOutside);
  onBeforeUnmount(() => {
    document.removeEventListener('click', handleClickOutside);
    window.removeEventListener('scroll', updateDropdownPosition);
    window.removeEventListener('resize', updateDropdownPosition);
  });
});

defineExpose({ focus: () => input.value?.focus() });
</script>

<template>
  <div class="combobox" ref="containerRef">
    <div class="combobox__input-container">
      <div v-if="$slots.icon" class="combobox__icon-container">
        <slot name="icon"></slot>
      </div>
      
      <input
        ref="input"
        type="text"
        :value="displayValue"
        @input="handleInput"
        @focus="handleFocus"
        @blur="handleBlur"
        :id="id"
        :placeholder="placeholder"
        :required="required"
        :disabled="disabled"
        :class="[
          'combobox__input',
          $slots.icon ? 'combobox__input--with-icon' : '',
          clearable ? 'combobox__input--clearable' : '',
          disabled ? 'combobox__input--disabled' : '',
          loading ? 'combobox__input--loading' : ''
        ]"
      />
      
      <div class="combobox__actions">
        <button
          v-if="clearable && modelValue && !disabled && !loading"
          type="button"
          class="combobox__clear-button"
          @click="handleClear"
        >
          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" class="combobox__clear-icon">
            <path d="M6.28 5.22a.75.75 0 00-1.06 1.06L8.94 10l-3.72 3.72a.75.75 0 101.06 1.06L10 11.06l3.72 3.72a.75.75 0 101.06-1.06L11.06 10l3.72-3.72a.75.75 0 00-1.06-1.06L10 8.94 6.28 5.22z" />
          </svg>
        </button>
        
        <div v-if="loading" class="combobox__spinner">
          <svg class="combobox__spinner-icon" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
            <circle class="combobox__spinner-circle" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
            <path class="combobox__spinner-path" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
          </svg>
        </div>
        
        <div class="combobox__caret">
          <svg 
            class="combobox__caret-icon"
            :class="{ 'combobox__caret-icon--open': isOpen }"
            xmlns="http://www.w3.org/2000/svg" 
            viewBox="0 0 20 20" 
            fill="currentColor"
          >
            <path fill-rule="evenodd" d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" clip-rule="evenodd" />
          </svg>
        </div>
      </div>
    </div>

    <div v-show="isOpen && !disabled" class="combobox__dropdown">
      <div class="combobox__options">
        <template v-if="filteredOptions?.length">
          <button
            v-for="option in filteredOptions"
            :key="option?.value"
            type="button"
            :class="[combobox__option, option?.value === modelValue ? 'combobox__option--selected' : 'combobox__option']"
            @click="handleSelect(option)"
          >
            <div v-if="showProfilePicture" class="combobox__option-avatar">
              <img v-if="option?.profilePicture" :src="option?.profilePicture" alt="" class="combobox__option-avatar-img">
              <User v-else class="w-full h-full p-1 text-white" />
            </div>
            <span class="combobox__option-label truncate">{{ option?.label || option?.displayValue }}</span>
          </button>
        </template>
        
        <button
          v-if="showCreateOption"
          type="button"
          class="combobox__option combobox__option--create"
          @click="handleCreate"
        >
          <span class="combobox__option-label truncate">{{ createText }}: "{{ searchQuery }}"</span>
        </button>
        
        <div v-if="!filteredOptions?.length && !showCreateOption && searchQuery" class="combobox__no-options">
          {{ noResultsText }}
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped lang="postcss">
.combobox {
  @apply relative w-full;
}

.combobox__input-container {
  @apply relative isolate;
}

.combobox__icon-container {
  @apply absolute inset-y-0 left-0 pl-3.5 flex items-center pointer-events-none
    text-white/80 z-10;
}

.combobox__input {
  @apply truncate w-full px-4 py-2.5 text-sm rounded-[13px] h-11
    bg-echoes-background-black
    text-white
    border border-echoes-border-gray
    transition-all duration-200 ease-in-out;
  
  &:hover {
    @apply bg-echoes-background-black
      border-white/20
      text-white;
  }

  &:focus {
    @apply bg-echoes-background-black border-echoes-primary outline-none;
  }

  &::placeholder {
    @apply transition-all duration-200 text-white/50 hover:text-white/80;
  }

  &:focus::placeholder {
    @apply opacity-0 translate-x-1;
  }
}

.combobox__input--with-icon {
  @apply pl-11;
}

.combobox__input--disabled {
  @apply opacity-60 cursor-not-allowed;

  &:hover {
    @apply bg-echoes-background-black
      border-echoes-border-gray
      shadow-none;
  }
}

.combobox__input--clearable {
  @apply pr-12;
}

.combobox__input--loading {
  @apply opacity-50 cursor-not-allowed;
}

.combobox__actions {
  @apply absolute inset-y-0 right-0 flex items-center gap-1 pr-3 pointer-events-none;
}

.combobox__clear-button {
  @apply rounded-full hover:bg-echoes-background-gray/50
    transition-colors duration-150 pointer-events-auto;
}

.combobox__clear-icon {
  @apply w-4 h-4 text-white/80;
}

.combobox__spinner {
  @apply flex items-center justify-center;
}

.combobox__spinner-icon {
  @apply w-4 h-4 text-echoes-primary animate-spin;
}

.combobox__spinner-circle {
  @apply opacity-25;
}

.combobox__spinner-path {
  @apply opacity-75;
}

.combobox__caret {
  @apply flex items-center ml-2 flex-shrink-0;
}

.combobox__caret-icon {
  @apply w-5 h-5 text-white/80 transition-transform duration-200;
}

.combobox__caret-icon--open {
  @apply transform rotate-180;
}

.combobox__dropdown {
  @apply absolute z-[9999] mt-1
    bg-echoes-background-black 
    border border-echoes-border-gray
    rounded-[13px] shadow-sm
    overflow-hidden
    left-0
    right-0
    top-full;
  width: 100%;
  max-width: 100%;
}

.combobox__options {
  @apply max-h-[160px] overflow-y-auto;
}

.combobox__option {
  @apply w-full px-4 py-2.5 text-left text-xs md:text-sm h-11
    text-white 
    transition-colors duration-150 ease-in-out
    flex items-center overflow-hidden;

  &:hover {
    @apply bg-echoes-background-gray/30;
  }
}

.combobox__option--selected {
  @apply w-full px-4 py-2.5 text-left text-xs md:text-sm h-11 bg-echoes-primary
    text-white 
    transition-colors duration-150 ease-in-out
    flex items-center overflow-hidden;

  &:hover {
    @apply bg-echoes-primary/30;
  }
}

.combobox__option--create {
  @apply text-echoes-primary font-medium;

  &:hover {
    @apply bg-echoes-primary/10;
  }
}

.combobox__option-avatar {
  @apply w-6 h-6 rounded-full overflow-hidden bg-echoes-primary mr-2 flex-shrink-0;
}

.combobox__option-avatar-img {
  @apply w-full h-full object-cover;
}

.combobox__option-label {
  @apply flex-1 overflow-hidden;
}

.combobox__no-options {
  @apply px-4 py-2.5 text-xs md:text-sm text-white/80;
}
</style> 